import { useAppSelector } from 'appState/store'
import { ICall } from 'features/calls/types'
import { useAppealEnumDescriptions } from 'features/claims/hooks/useAppealEnumDescriptions'
import { useLocation } from 'react-router-dom'
import { Badge, CardTabs } from '@outbound-ai/ursa'
import { groupMany, mapFilteredSlotObjects } from 'utils/dataExtractor'
import { ClaimSnapshot } from './Internal'
import { AppealInfo, InsuranceInfo, PatientInfo, ProviderInfo, snapShotInfo } from './Internal/EntityVersions'

interface IClaimDetailsPayerProps {
  call?: ICall
  slotData: any
}

export const ProviderRecord = ({ call, slotData }: IClaimDetailsPayerProps) => {
  const appealEnumDescriptions = useAppealEnumDescriptions()
  const ascEnabled = useAppSelector((state) => state.auth.ascEnabled)
  const location = useLocation()
  const { pathname } = location
  const currentUrl = pathname.split('/')
  const isCallSummary = currentUrl.includes('calls-summary')

  const filteredPatientInfo = mapFilteredSlotObjects(slotData, PatientInfo)
  const filteredProviderInfo = mapFilteredSlotObjects(slotData, ProviderInfo)
  const filteredInsuranceInfo = mapFilteredSlotObjects(slotData, InsuranceInfo)
  const filteredSnapshotInfo = mapFilteredSlotObjects(slotData, snapShotInfo)
  const filteredAppealInfo = mapFilteredSlotObjects(slotData, AppealInfo, appealEnumDescriptions)
  const billedAmount = mapFilteredSlotObjects(slotData, ['claim_billed_amount'])

  const groupedPatientInfo = groupMany(filteredPatientInfo, [
    {
      slots: ['patient_first_name', 'patient_last_name'],
      options: { newSlot: 'patient_name', description: 'Patient Name' },
    },
    {
      slots: ['patient_dob'],
      options: { newSlot: 'patient_dob', description: 'DOB' },
    },
    { slots: ['patient_sex'], options: { newSlot: 'patient_sex', description: 'Gender' } },
    {
      slots: ['patient_street1', 'patient_street2', 'patient_city', 'patient_state', 'patient_zip'],
      options: { newSlot: 'patient_address', description: 'Patient Address' },
      valueFromLines: [
        { of: ['patient_street1'], joiner: ' ' },
        { of: ['patient_street2'], joiner: ' ' },
        { of: ['patient_city', 'patient_state', 'patient_zip'], joiner: ', ' },
      ],
    },
    { slots: ['patient_id'], options: { newSlot: 'patient_id', description: 'Patient Id' } },
    { slots: ['subscriber_id'], options: { newSlot: 'subscriber_id', description: 'Subscriber Id' } },
  ])

  const groupedSnapshotInfo = groupMany(filteredSnapshotInfo, [
    { slots: ['member_id'], options: { newSlot: 'member_id', description: 'Member Id' } },
    {
      slots: ['patient_first_name', 'patient_last_name'],
      options: { newSlot: 'patient_name', description: 'Patient Name' },
    },
    { slots: ['patient_dob'], options: { newSlot: 'patient_dob', description: 'Patient DOB' } },
    {
      slots: ['date_of_provision_or_admission'],
      options: { newSlot: 'date_of_provision_or_admission', description: 'Date of Service' },
    },
    { slots: ['unique_identifier'], options: { newSlot: 'unique_identifier', description: 'Unique ID' } },
    { slots: ['biller_name'], options: { newSlot: 'biller_name', description: 'Biller Name' } },
    { slots: ['insurer_phone_number'], options: { newSlot: 'insurer_phone_number', description: 'Phone Number' } },
    { slots: ['outstanding_balance'], options: { newSlot: 'outstanding_balance', description: 'Balance Due' } },
    { slots: ['insurer_claim_id'], options: { newSlot: 'insurer_claim_id', description: 'Claim ID' } },
    {
      slots: ['callInformation_taxId'],
      options: { newSlot: 'callInformation_taxId ', description: 'Provider Tax ID' },
    },
    { slots: ['biller_npi'], options: { newSlot: 'biller_npi', description: 'Provider NPI' } },
  ])

  const groupedSubscriberInfo = groupMany(filteredInsuranceInfo, [
    { slots: ['member_id'], options: { newSlot: 'member_id', description: 'Member Id' } },

    {
      slots: ['subscriber_first_name', 'subscriber_last_name'],
      options: { newSlot: 'subscriber_name', description: 'Subscriber Name' },
    },
    { slots: ['subscriber_dob'], options: { newSlot: 'subscriber_dob', description: 'Subscriber DOB' } },
    {
      slots: ['subscriber_relationship'],
      options: { newSlot: 'subscriber_relationship', description: 'Subscriber Relationship' },
    },
    { slots: ['subscriber_zip_code'], options: { newSlot: 'subscriber_zip_code', description: 'Subscriber Zip Code' } },
    { slots: ['insurer_name'], options: { newSlot: 'insurer_name', description: 'Payer' } },
    { slots: ['insurer_plan_name'], options: { newSlot: 'insurer_plan_name', description: 'Plan Name' } },
    { slots: ['insurer_id'], options: { newSlot: 'insurer_id', description: 'Plan ID' } },
    { slots: ['group_id'], options: { newSlot: 'group_id', description: 'Group Number' } },
    {
      slots: ['insurer_phone_number'],
      options: { newSlot: 'insurer_phone_number', description: 'Insurer Phone Number' },
    },
    { slots: ['subscriber_id'], options: { newSlot: 'subscriber_id', description: 'Subscriber ID' } },
  ])

  const groupProviderInfo = groupMany(filteredProviderInfo, [
    {
      slots: ['rendering_provider'],
      options: { newSlot: 'rendering_provider', description: 'Rendering Provider' },
    },
    {
      slots: ['rendering_provider_npi'],
      options: { newSlot: 'rendering_provider_npi', description: 'Rendering Provider NPI' },
    },
    { slots: ['biller_name'], options: { newSlot: 'biller_name', description: 'Billing Entity' } },
    {
      slots: ['provider_street1', 'provider_street2', 'provider_city', 'provider_state', 'provider_zip_code'],
      options: { newSlot: 'provider_address', description: 'Address' },
      valueFromLines: [
        { of: ['provider_street1'], joiner: ' ' },
        { of: ['provider_street2'], joiner: ' ' },
        { of: ['provider_city', 'provider_state', 'provider_zip_code'], joiner: ', ' },
      ],
    },
    { slots: ['biller_npi'], options: { newSlot: 'biller_npi', description: 'Billing NPI' } },
    { slots: ['callInformation_taxId'], options: { newSlot: 'callInformation_taxId', description: 'Tax ID' } },
    { slots: ['ptan'], options: { newSlot: 'ptan', description: 'PTAN' } },
    { slots: ['location'], options: { newSlot: 'location', description: 'Location' } },
  ])

  const groupAppealInfo = groupMany(filteredAppealInfo, [
    {
      slots: ['payer_appeal_case_number'],
      options: { newSlot: 'payer_appeal_case_number', description: 'Payer Appeal Case Number' },
    },
    {
      slots: ['appeal_submission_date'],
      options: { newSlot: 'appeal_submission_date', description: 'Appeal Submission Date' },
    },
    {
      slots: ['appeal_submission_method'],
      options: { newSlot: 'appeal_submission_method', description: 'Appeal Submission Method' },
    },
    {
      slots: ['appeal_submission_tracking_number'],
      options: { newSlot: 'appeal_submission_tracking_number', description: 'Appeal Submission Tracking Number' },
    },
    {
      slots: ['provider_appeal_fax_number'],
      options: { newSlot: 'provider_appeal_fax_number', description: 'Provider Appeal Fax Number' },
    },
    {
      slots: [
        'provider_appeal_street1',
        'provider_appeal_street2',
        'provider_appeal_city',
        'provider_appeal_state',
        'provider_appeal_zipcode',
      ],
      options: { newSlot: 'provider_appeal_address', description: 'Provider Appeal Address' },
      valueFromLines: [
        { of: ['provider_appeal_street1'], joiner: ' ' },
        { of: ['provider_appeal_street2'], joiner: ' ' },
        { of: ['provider_appeal_city', 'provider_appeal_state', 'provider_appeal_zipcode'], joiner: ', ' },
      ],
    },
    {
      slots: ['level_of_appeal'],
      options: { newSlot: 'level_of_appeal', description: 'Level of Appeal' },
    },
    {
      slots: ['appeal_phone_number'],
      options: { newSlot: 'appeal_phone_number', description: 'Appeal Phone Number' },
    },
  ])

  return (
    <>
      <CardTabs
        title="Provider Record"
        tabs={[
          {
            tabs: 'Claim Snapshot',
            tabContent: (
              <ClaimSnapshot
                data={groupedSnapshotInfo} // SlotData[]
                billedAmount={billedAmount[0]}
                includeSlots={[
                  'member_id',
                  'patient_name',
                  'patient_dob',
                  'patient_sex',
                  'date_of_provision_or_admission',
                  'unique_identifier',
                  'biller_name',
                  'insurer_phone_number',
                  'outstanding_balance',
                  'insurer_claim_id',
                  'callInformation_taxId',
                  'biller_npi',
                ]} // SlotData or SlotData[]
              />
            ),
          },
          {
            tabs: 'Patient',
            tabContent: (
              <ClaimSnapshot
                data={groupedPatientInfo}
                billedAmount={billedAmount[0]}
                includeSlots={[
                  'patient_name',
                  'patient_dob',
                  'patient_sex',
                  'patient_address',
                  'patient_id',
                  'subscriber_id',
                ]}
              />
            ),
          },
          {
            tabs: 'Provider',
            tabContent: (
              <ClaimSnapshot
                data={groupProviderInfo}
                billedAmount={billedAmount[0]}
                includeSlots={[
                  'rendering_provider',
                  'rendering_provider_npi',
                  'biller_name',
                  'provider_address',
                  'biller_npi',
                  'callInformation_taxId',
                  'ptan',
                  'location',
                  'provider_zip_code',
                ]}
              />
            ),
          },
          {
            tabs: 'Insurance',
            tabContent: (
              <ClaimSnapshot
                data={groupedSubscriberInfo}
                billedAmount={billedAmount[0]}
                includeSlots={[
                  'member_id',
                  'insurer_name',
                  'insurer_plan_name',
                  'insurer_id',
                  'group_id',
                  'subscriber_id',
                  'insurer_phone_number',
                  'subscriber_name',
                  'subscriber_dob',
                  'subscriber_sex',
                  'subscriber_relationship',
                ]}
              />
            ),
          },
          // Appeal tab is only shown for customers when it is enabled
          ...(ascEnabled
            ? [
                {
                  tabs: 'Appeal',
                  tabContent: (
                    <ClaimSnapshot
                      data={groupAppealInfo}
                      billedAmount={billedAmount[0]}
                      includeSlots={[
                        'payer_appeal_case_number',
                        'appeal_submission_date',
                        'appeal_submission_method',
                        'appeal_submission_tracking_number',
                        'provider_appeal_fax_number',
                        'provider_appeal_address',
                        'level_of_appeal',
                        'appeal_phone_number',
                      ]}
                    />
                  ),
                },
              ]
            : []),
        ]}
        action={
          !isCallSummary ? (
            <Badge variant="tag" fill="light" color="grey">
              Batch {mapFilteredSlotObjects(slotData, ['batch_code'])[0]?.value ?? ''}
            </Badge>
          ) : (
            <>
              {call?.batchCode && (
                <Badge variant="tag" fill="light" type="grey">
                  Batch {call?.batchCode}
                </Badge>
              )}
            </>
          )
        }
      />
    </>
  )
}
